import { computed, ref, watch } from 'vue';
import { useFormErrors } from '@/composables/useFormErrors';
import { useMyStudentProfileForm } from '@/composables/queries/useMyStudentProfileForm';
import { useStudentProfileFormMutations } from '@/composables/mutations/useStudentProfileFormMutations';
import { missingRequiredErrors, valueErrorKey, type CustomFieldInputValue } from '@/utils/customFields';
import {
  builtInValues, editableProfileEntries, profileMaxLengths, profileReminderText, profileValuesPayload, readOnlyProfileRows,
  requestedFieldTags, syncProfileDraft, type StudentProfileField,
} from '@/utils/studentProfileForm';

export type StudentProfileSaveResult =
  | { ok: true; builtIn: Record<string, string | null> }
  | { ok: false; message: string };

/** Shown when the errors sit on the fields themselves. */
export const CHECK_FIELDS_MESSAGE = 'Check the fields marked in red.';

/** Shown when a refused field is no longer one the student fills in. */
export const FORM_CHANGED_MESSAGE = 'Your school changed which details you can fill in. The form has been updated.';

/**
 * The student's own profile form: its inputs, kept in step with the stored
 * values field by field (a field the student is editing keeps their edit,
 * the others follow the stored copy), required checks, and the save, with
 * server errors on the field they belong to. Only fields the student changed
 * are sent.
 */
export function useStudentProfileFormDraft() {
  const { fields, missingRequired, loading, error, refetch } = useMyStudentProfileForm();
  const { saveMine } = useStudentProfileFormMutations();
  const { serverErrors, handleServerErrors, clearErrors, getFirstErrorMessage } = useFormErrors();

  const entries = computed(() => editableProfileEntries(fields.value));
  const readOnly = computed(() => readOnlyProfileRows(fields.value));
  const reminder = computed(() => profileReminderText(missingRequired.value));
  const maxLengths = computed(() => profileMaxLengths(fields.value));
  const tags = computed(() => requestedFieldTags(fields.value));

  const model = ref<Record<string, CustomFieldInputValue>>({});
  /** The stored values the model was last brought up to date with. */
  const base = ref<Record<string, CustomFieldInputValue>>({});

  function sync(next: ReadonlyArray<StudentProfileField>) {
    const draft = syncProfileDraft(next, { model: model.value, base: base.value });
    model.value = draft.model;
    base.value = draft.base;
  }
  watch(fields, sync, { immediate: true });

  const payload = () => profileValuesPayload(fields.value, { model: model.value, base: base.value });
  const changed = computed(() => payload().length > 0);

  const clientErrors = ref<Record<string, string>>({});
  const errors = computed<Record<string, string>>(() => Object.fromEntries(entries.value.map(({ field }) => [
    field.key,
    clientErrors.value[field.key] ?? serverErrors.value[valueErrorKey(field.key)]?.[0] ?? '',
  ])));

  const saving = ref(false);

  async function save(): Promise<StudentProfileSaveResult> {
    clearErrors();
    const values = payload();
    // Required only for what is being sent, as on the server: an untouched
    // empty field (a NIN the student does not know yet) never blocks saving
    // the rest, which a data request may be waiting on.
    const sentKeys = new Set(values.map(v => v.key));
    clientErrors.value = missingRequiredErrors(entries.value.filter(({ field }) => sentKeys.has(field.key)), model.value);
    if (Object.keys(clientErrors.value).length) return { ok: false, message: CHECK_FIELDS_MESSAGE };
    if (!values.length) return { ok: true, builtIn: {} };

    const sent = { ...model.value };
    saving.value = true;
    try {
      const result = await saveMine.mutate({ values });
      // What was sent is stored now; anything typed since the save started
      // differs from it, so the refill below keeps it.
      base.value = { ...base.value, ...Object.fromEntries(values.map(v => [v.key, sent[v.key] ?? null])) };
      const saved = result?.data?.updateMyStudentProfileForm.fields;
      if (saved) sync(saved);
      return { ok: true, builtIn: builtInValues(values) };
    } catch (err) {
      handleServerErrors(err);
      const refused = entries.value.map(({ field }) => field.key).filter(key => !!serverErrors.value[valueErrorKey(key)]);
      if (refused.length) {
        // A field the school closed or locked since the form loaded: show the
        // form as it is now (the refill drops fields no longer offered).
        await Promise.resolve(refetch()).catch(() => {});
        const stillOffered = new Set(entries.value.map(({ field }) => field.key));
        return { ok: false, message: refused.every(key => stillOffered.has(key)) ? CHECK_FIELDS_MESSAGE : FORM_CHANGED_MESSAGE };
      }
      refetch()?.catch(() => {});
      console.error(err);
      return { ok: false, message: getFirstErrorMessage() || (err as Error)?.message || 'Please try again.' };
    } finally {
      saving.value = false;
    }
  }

  return { fields, entries, readOnly, reminder, maxLengths, tags, model, changed, errors, saving, loading, error, refetch, save };
}
